// Questions: why a layer is off, and what this map does not claim to know.
//
// The page is prose in faq.html and needs no JavaScript to be readable. This
// adds one thing to it, and it is worth the request.
//
// Three of the answers explain why something is *currently* unavailable — a
// council that has not registered its taxi ranks, a feed that refuses our
// server, an unreliable free AIS relay. Those are all states that end. A FAQ
// that goes on insisting fuel prices are blocked six months after they started
// working is worse than no FAQ: it is a page confidently telling the reader
// something false, on a site whose whole argument is that it says what it
// actually knows.
//
// So each answer with a matching backend layer carries `data-provider`, and
// this checks whether that layer now reports itself working. If it does, the
// answer gets a banner saying so. If the check fails — offline, API down,
// old backend — nothing happens and the prose stands. Enhancement only:
// there is no state in which this page needs the network to be useful.
//
// A separate Vite entry, sharing nothing with the map.

import './faq-page.css';
import { initAnalytics } from './analytics.ts';
import { BASE } from './http.ts';

interface StatusItem {
  providerId: string;
  configured: boolean;
  summary: string;
}

/**
 * Mark any answer whose layer has started working.
 *
 * Deliberately one-directional. A layer reporting `configured: false` is the
 * state the prose already describes, so there is nothing to add — and adding
 * "still broken" banners would turn a reference page into a status dashboard,
 * which is what /status.html is for.
 */
function markResolved(items: StatusItem[]): void {
  const byId = new Map(items.map((i) => [i.providerId, i]));

  for (const section of document.querySelectorAll<HTMLElement>('.q[data-provider]')) {
    const item = byId.get(section.dataset.provider ?? '');
    if (!item?.configured) continue;

    const banner = document.createElement('p');
    banner.className = 'resolved';
    banner.setAttribute('role', 'status');
    // The provider's own summary, so the banner carries the live count rather
    // than a second-hand claim that it works. "18 taxi ranks in 4 towns" is
    // evidence; "this is now fixed" is an assertion.
    banner.textContent = `This has since been resolved — ${item.summary}. `
      + 'The answer below is kept for the explanation.';
    section.prepend(banner);
    section.classList.add('is-resolved');
  }
}

async function checkLayers(): Promise<void> {
  try {
    const res = await fetch(`${BASE}/api/v1/status`, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(8_000),
    });
    if (!res.ok) return;
    const data = await res.json() as { items?: StatusItem[] };
    if (Array.isArray(data.items)) markResolved(data.items);
  } catch {
    // Offline, blocked, or an older backend. The page is complete without it.
  }
}

/** Which build is serving, matching the Sources page footer. */
function renderBuild(): void {
  const el = document.getElementById('build');
  if (!el) return;
  const { version, commit } = __BUILD__;
  el.innerHTML = `site <strong>v${version}</strong> <code>${commit}</code>`;
}

initAnalytics();
renderBuild();
void checkLayers();
